import {
  Home as HomeIcon,
  Footprints,
  BedDouble,
  Scissors,
  Armchair,
  Target,
  Car,
  Moon,
  Stethoscope,
  Building2,
} from 'lucide-react';

/**
 * Canonical pet care service catalog.
 *
 * Single source of truth shared by:
 *  - Home hero "Select Service" dropdown
 *  - Navbar services dropdown
 *  - Caregiver "My Services" tagging
 *  - Find Care search filtering
 *
 * The `slug` is what travels in the URL (/search?service=pet-boarding) and must
 * stay stable — renaming a slug orphans existing caregiver service tags.
 */
export const SERVICE_CATALOG = [
  {
    slug: 'pet-daycare',
    label: 'Pet Daycare',
    description: 'Full-day care with activities and socialization',
    price: 'From ₹500/day',
    icon: Building2,
  },
  {
    slug: 'dog-walking',
    label: 'Dog Walking',
    description: 'Regular walks and outdoor exercise',
    price: 'From ₹200/walk',
    icon: Footprints,
  },
  {
    slug: 'pet-boarding',
    label: 'Pet Boarding',
    description: 'Overnight stays in caregiver homes',
    price: 'From ₹800/night',
    icon: BedDouble,
  },
  {
    slug: 'pet-grooming',
    label: 'Pet Grooming',
    description: 'Professional bathing and styling',
    price: 'From ₹1000/session',
    icon: Scissors,
  },
  {
    slug: 'pet-sitting',
    label: 'Pet Sitting',
    description: 'In-home pet care and companionship',
    price: 'From ₹600/visit',
    icon: Armchair,
  },
  {
    slug: 'house-sitting',
    label: 'House Sitting',
    description: 'Stay at your home while you travel',
    price: 'From ₹1200/night',
    icon: HomeIcon,
  },
  {
    slug: 'pet-training',
    label: 'Pet Training',
    description: 'Behaviour and obedience training',
    price: 'From ₹1500/session',
    icon: Target,
  },
  {
    slug: 'pet-transportation',
    label: 'Pet Transportation',
    description: 'Safe transport to vet or grooming',
    price: 'From ₹300/trip',
    icon: Car,
  },
  {
    slug: 'overnight-care',
    label: 'Overnight Care',
    description: 'Dedicated overnight supervision',
    price: 'From ₹900/night',
    icon: Moon,
  },
  {
    slug: 'veterinary-visits',
    label: 'Veterinary Visits',
    description: 'Accompanied trips to the vet',
    price: 'From ₹400/visit',
    icon: Stethoscope,
  },
];

export const SERVICE_SLUGS = SERVICE_CATALOG.map((service) => service.slug);

/** Turn any service label or slug into its canonical slug. */
export const toServiceSlug = (value) => {
  if (!value || typeof value !== 'string') return '';
  const normalized = value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  return SERVICE_SLUGS.includes(normalized) ? normalized : '';
};

export const getService = (slugOrLabel) => {
  const slug = toServiceSlug(slugOrLabel);
  return SERVICE_CATALOG.find((service) => service.slug === slug) || null;
};

export const getServiceLabel = (slugOrLabel) => getService(slugOrLabel)?.label || '';

/**
 * Normalize the many shapes a caregiver's services can arrive in
 * (service docs from the `services` collection, or a plain array on the user doc)
 * into a deduped list of canonical slugs.
 */
export const toServiceSlugs = (services) => {
  if (!Array.isArray(services)) return [];

  const slugs = services
    .map((entry) => {
      if (typeof entry === 'string') return toServiceSlug(entry);
      if (entry && typeof entry === 'object') {
        return toServiceSlug(entry.slug || entry.serviceSlug || entry.name || entry.label);
      }
      return '';
    })
    .filter(Boolean);

  return [...new Set(slugs)];
};

export default SERVICE_CATALOG;
